import {
  cleanupPendingMCPServerForUrl,
  type OAuthReturnTo,
} from '@littlebird/mcp';
import { toast } from 'sonner';

import { backendApis } from '@/backendApis';
import { ModalsManager } from '@/components/modals/modalsManager';
import { FEATURES, hasFeature } from '@/features/feature-flags';
import type { IntegrationTarget } from '@/features/integrations/integrations.types';
import { MCPAddServerModal } from '@/features/mcp/MCPAddServerModal';
import { subscriptionStore } from '@/features/subscription/subscription.store';
import { setHashInElectron } from '@/hooks/useHashRoute';
import { connectOAuthIntegration } from '@/integrations/integration-handlers';
import { currentPageReturnTo } from '@/integrations/oauth-return';
import { trackEvent } from '@/utils/analytics';
import { handleExternalUrlClick } from '@/utils/platform';

const TOOLKIT_CONNECTED_EVENTS: Record<
  string,
  'GMAIL_CONNECTED' | 'GOOGLE_CALENDAR_CONNECTED'
> = {
  gmail: 'GMAIL_CONNECTED',
  googlecalendar: 'GOOGLE_CALENDAR_CONNECTED',
};

export const trackToolkitConnected = (
  toolkitName: string,
  source: string
): void => {
  const event = TOOLKIT_CONNECTED_EVENTS[toolkitName];
  if (!event) return;
  trackEvent(event, { source: source.split(':')[0], source_detail: source });
};

export type OnDeviceCardType = 'builtin' | 'catalog' | 'bundled';
export type OnDeviceSurface =
  | 'integrations_modal'
  | 'settings'
  | 'home'
  | 'feed';

export const trackOnDeviceTabViewed = (): void => {
  trackEvent('ON_DEVICE_INTEGRATIONS_VIEWED');
};

export const trackOnDeviceConnectClicked = (
  integrationId: string,
  cardType: OnDeviceCardType,
  surface: OnDeviceSurface
): void => {
  trackEvent('ON_DEVICE_INTEGRATION_CONNECT_STARTED', {
    integration_id: integrationId,
    card_type: cardType,
    surface,
  });
};

export const trackCustomMcpServerAdded = (
  integrationId: string | undefined,
  cardType: OnDeviceCardType | 'custom',
  transportKind: string
): void => {
  trackEvent('CUSTOM_MCP_SERVER_ADDED', {
    integration_id: integrationId,
    card_type: cardType,
    transport_kind: transportKind,
  });
};

/** Fires whenever a pasted MCP config is successfully parsed and applied to the add-server form. */
export const trackCustomMcpServerConfigPasted = (): void => {
  trackEvent('CUSTOM_MCP_SERVER_CONFIG_PASTED');
};

/** Fires whenever a pasted MCP config is rejected by the parser (invalid JSON, remote URL, no entry). */
export const trackCustomMcpServerConfigRejected = (): void => {
  trackEvent('CUSTOM_MCP_SERVER_CONFIG_REJECTED');
};

export function openMcpReconnectModal(
  result: {
    redirect_url?: string | null;
    server_id: string;
    flow_id?: string | null;
  },
  serverName: string,
  options?: {
    forceInBrowser?: boolean;
    returnTo?: OAuthReturnTo;
    onSettled?: (success: boolean) => void;
  }
): void {
  if (!result.redirect_url) return;
  handleExternalUrlClick(result.redirect_url, {
    forceInBrowser: options?.forceInBrowser,
  });
  ModalsManager.show(MCPAddServerModal, {
    reconnectServerId: result.server_id,
    reconnectServerName: serverName,
    reconnectFlowId: result.flow_id ?? undefined,
    returnTo: options?.returnTo,
    onOAuthSettled: options?.onSettled,
  });
}

export const hasIntegrationAccess = (): boolean =>
  subscriptionStore.isActive || hasFeature(FEATURES.FREE_PLAN_INTEGRATIONS);

export async function connectIntegration(
  target: IntegrationTarget,
  source: string,
  options?: {
    onUpgrade?: () => void;
    returnTo?: OAuthReturnTo;
    // Called when a connect or reconnect finishes, which for MCP is in its dialog, after this returns
    onSettled?: (success: boolean) => void;
    // An MCP connect or reconnect goes to the consent page in this tab; a browser can block a new tab opened
    // after the consent URL loads
    sameTab?: boolean;
  }
) {
  if (
    !hasIntegrationAccess() &&
    !hasFeature(FEATURES.WEB_ONBOARDING_FEED) &&
    target.requiresPaidPlan
  ) {
    trackEvent('UPGRADE_BUTTON_CLICKED', { source });
    if (options?.onUpgrade) {
      options.onUpgrade();
    } else {
      setHashInElectron('settings/Subscription');
    }
    return;
  }

  if (target.kind === 'integration') {
    const toastId = `connect-${target.toolkitName}`;
    toast.loading(`Connecting ${target.displayName}…`, { id: toastId });
    await connectOAuthIntegration(
      target.toolkitName,
      target.displayName,
      undefined,
      (success) => {
        if (success) trackToolkitConnected(target.toolkitName, source);
        options?.onSettled?.(success);
      },
      options?.returnTo,
      target.reconnectAccountId
    );
    toast.dismiss(toastId);
    return;
  }

  if (target.reconnectServerId !== undefined) {
    try {
      if (options?.sameTab) {
        const result = await backendApis.mcp.reconnectServer(
          target.reconnectServerId,
          options.returnTo ?? currentPageReturnTo()
        );
        if (result.redirect_url) window.location.assign(result.redirect_url);
        return;
      }
      const result = await backendApis.mcp.reconnectServer(
        target.reconnectServerId
      );
      openMcpReconnectModal(result, target.displayName, {
        returnTo: options?.returnTo,
        onSettled: options?.onSettled,
      });
    } catch (error) {
      console.error('MCP reconnect error:', error);
      toast.error(`Failed to reconnect ${target.displayName}`);
    }
    return;
  }
  if (options?.sameTab) {
    try {
      await cleanupPendingMCPServerForUrl(backendApis.mcp, target.url);
      const oauth = await backendApis.mcp.initiateOAuth(
        target.url,
        target.displayName,
        options.returnTo ?? currentPageReturnTo()
      );
      window.location.assign(oauth.redirect_url);
    } catch (error) {
      console.error('MCP connect error:', error);
      toast.error(`Failed to connect ${target.displayName}`);
    }
    return;
  }
  ModalsManager.show(MCPAddServerModal, {
    initialUrl: target.url,
    initialName: target.displayName,
    returnTo: options?.returnTo,
    onOAuthSettled: options?.onSettled,
  });
}
